import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Input, ColorPicker, Space, Row, Col, Divider } from 'antd';
import { DeleteOutlined, PlusOutlined, CloseOutlined } from '@ant-design/icons';
import { message } from '../utils/message';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface ColorScheme {
  id: number;
  name: string;
  displayName: string;
  primaryColor: string;
  secondaryColor: string;
  isActive: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

interface ColorSchemeManagerProps {
  visible: boolean;
  onCancel: () => void;
  onSchemeUpdated?: () => void;
}

const ColorSchemeManager: React.FC<ColorSchemeManagerProps> = ({
  visible,
  onCancel,
  onSchemeUpdated
}) => {
  const [colorSchemes, setColorSchemes] = useState<ColorScheme[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  // 컬러스킴 목록 조회
  const fetchColorSchemes = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/cms/color-schemes`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();

      if (data.success) {
        const schemes = data.data || [];
        setColorSchemes(schemes);

        // 폼 초기값 설정
        const initialValues: any = {};
        schemes.forEach((scheme: ColorScheme) => {
          initialValues[`name_${scheme.id}`] = scheme.name;
          initialValues[`displayName_${scheme.id}`] = scheme.displayName;
          initialValues[`primaryColor_${scheme.id}`] = scheme.primaryColor;
          initialValues[`secondaryColor_${scheme.id}`] = scheme.secondaryColor;
        });
        form.setFieldsValue(initialValues);
      } else {
        message.error('컬러스킴 목록 조회에 실패했습니다.');
      }
    } catch (error) {
      console.error('컬러스킴 조회 실패:', error);
      message.error('컬러스킴 목록 조회 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 저장 처리
  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // console.log('폼 값:', values);

      // 각 컬러스킴 업데이트
      const updatePromises = colorSchemes.map(async (scheme) => {
        // hidden 필드는 폼에서 값을 가져오지 못할 수 있으므로 scheme에서 직접 가져옴
        const name = values[`name_${scheme.id}`] || scheme.name;
        const displayName = values[`displayName_${scheme.id}`];
        const primaryColor = values[`primaryColor_${scheme.id}`];
        const secondaryColor = values[`secondaryColor_${scheme.id}`];

        const payload = {
          name,
          displayName,
          primaryColor: typeof primaryColor === 'string' ? primaryColor : primaryColor.toHexString(),
          secondaryColor: typeof secondaryColor === 'string' ? secondaryColor : secondaryColor.toHexString(),
        };

        // console.log(`컬러스킴 ${scheme.id} 업데이트:`, payload);

        const token = localStorage.getItem('accessToken');
        const response = await fetch(`${API_BASE_URL}/api/cms/color-schemes/${scheme.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();
        // console.log(`컬러스킴 ${scheme.id} 응답:`, result);
        return result;
      });

      const results = await Promise.all(updatePromises);

      const allSuccess = results.every(result => result.success);

      if (allSuccess) {
        message.success('성공적으로 저장되었습니다.');
        await fetchColorSchemes();
        onSchemeUpdated?.();
        onCancel();
      } else {
        const errors = results.filter(r => !r.success).map(r => r.message).join(', ');
        console.error('저장 실패:', errors);
        message.error(`일부 컬러스킴 저장에 실패했습니다: ${errors}`);
      }
    } catch (error) {
      console.error('컬러스킴 저장 실패:', error);
      message.error('컬러스킴 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 컬러스킴 삭제
  const handleDelete = async (id: number) => {
    try {
      // 현재 폼의 값을 저장
      const currentFormValues = form.getFieldsValue();

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/cms/color-schemes/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        message.success('삭제가 완료되었습니다.');

        // 서버에서 최신 목록 가져오기
        const listResponse = await fetch(`${API_BASE_URL}/api/cms/color-schemes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const listData = await listResponse.json();

        if (listData.success) {
          const schemes = listData.data || [];
          setColorSchemes(schemes);

          // 삭제된 항목을 제외한 값들을 복원
          const initialValues: any = {};
          schemes.forEach((scheme: ColorScheme) => {
            // 기존에 편집하던 값이 있으면 사용, 없으면 서버 값 사용
            initialValues[`name_${scheme.id}`] = currentFormValues[`name_${scheme.id}`] || scheme.name;
            initialValues[`displayName_${scheme.id}`] = currentFormValues[`displayName_${scheme.id}`] || scheme.displayName;
            initialValues[`primaryColor_${scheme.id}`] = currentFormValues[`primaryColor_${scheme.id}`] || scheme.primaryColor;
            initialValues[`secondaryColor_${scheme.id}`] = currentFormValues[`secondaryColor_${scheme.id}`] || scheme.secondaryColor;
          });

          form.setFieldsValue(initialValues);
        }

        onSchemeUpdated?.();
      } else {
        message.error(data.message || '컬러스킴 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('컬러스킴 삭제 실패:', error);
      message.error('컬러스킴 삭제 중 오류가 발생했습니다.');
    }
  };

  // 새 컬러스킴 추가
  const handleAdd = async () => {
    try {
      setLoading(true);

      // 현재 폼의 값을 저장
      const currentFormValues = form.getFieldsValue();

      // 랜덤한 영문 식별자 생성 (타임스탬프 + 랜덤 문자열)
      const randomName = `colorscheme-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;

      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/api/cms/color-schemes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: randomName,
          displayName: `새 컬러스킴 ${colorSchemes.length + 1}`,
          primaryColor: '#1890ff',
          secondaryColor: '#13c2c2',
        }),
      });

      const data = await response.json();

      if (data.success) {
        message.success('성공적으로 추가되었습니다.');

        // 서버에서 최신 목록 가져오기
        const listResponse = await fetch(`${API_BASE_URL}/api/cms/color-schemes`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const listData = await listResponse.json();

        if (listData.success) {
          const schemes = listData.data || [];
          setColorSchemes(schemes);

          // 새로운 목록의 초기값 설정
          const initialValues: any = {};
          schemes.forEach((scheme: ColorScheme) => {
            // 기존에 편집하던 값이 있으면 사용, 없으면 서버 값 사용
            initialValues[`name_${scheme.id}`] = currentFormValues[`name_${scheme.id}`] || scheme.name;
            initialValues[`displayName_${scheme.id}`] = currentFormValues[`displayName_${scheme.id}`] || scheme.displayName;
            initialValues[`primaryColor_${scheme.id}`] = currentFormValues[`primaryColor_${scheme.id}`] || scheme.primaryColor;
            initialValues[`secondaryColor_${scheme.id}`] = currentFormValues[`secondaryColor_${scheme.id}`] || scheme.secondaryColor;
          });

          form.setFieldsValue(initialValues);
        }

        onSchemeUpdated?.();
      } else {
        message.error(data.message || '컬러스킴 추가에 실패했습니다.');
      }
    } catch (error) {
      console.error('컬러스킴 추가 실패:', error);
      message.error('컬러스킴 추가 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchColorSchemes();
    } else {
      // 모달이 닫힐 때 form 리셋
      form.resetFields();
    }
  }, [visible, form]);

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            색상 편집
          </div>
          <div style={{ fontSize: '13px', fontWeight: 'normal', color: '#a6a6a6' }}>
            레이아웃명에 사용될 색상 그룹을 편집합니다.
          </div>
          <div style={{ height: '50px'}}></div>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '50px' }}>
          <Button
            key="add"
            type="dashed"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            loading={loading}
          >
            추가
          </Button>
          <Space>
            <Button key="cancel" onClick={onCancel}>
              취소
            </Button>
            <Button key="save" type="primary" onClick={handleSave} loading={loading}>
              저장
            </Button>
          </Space>
        </div>
      }
      width={600}
      zIndex={1000}
      maskClosable={false}
      destroyOnHidden={true}
      styles={{
        mask: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)'
        },
        header: {
          color: '#ffffff'
        }
      }}
      closeIcon={<CloseOutlined style={{ color: '#ffffff', fontSize: '16px' }} />}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {/* 테이블 헤더 */}
        <Row gutter={16} style={{ marginBottom: 16, fontWeight: 'bold', color: '#ffffff' }}>
          <Col span={9}>색상명</Col>
          <Col span={7}>Primary</Col>
          <Col span={7}>Secondary</Col>
          <Col span={1}></Col>
        </Row>

        <Divider style={{ margin: '0 0 16px 0', borderColor: '#303030' }} />

        {/* 컬러스킴 목록 */}
        {colorSchemes.map((scheme, index) => (
          <div key={scheme.id}>
            <Row gutter={16} align="middle" style={{ marginBottom: index === colorSchemes.length - 1 ? 0 : 16 }}>
              {/* 영문 식별자는 숨김 처리 (자동 생성) */}
              <Form.Item
                name={`name_${scheme.id}`}
                hidden
              >
                <Input />
              </Form.Item>

              <Col span={9}>
                <Form.Item
                  name={`displayName_${scheme.id}`}
                  rules={[
                    { required: true, message: '한글 표시명을 입력해주세요.' }
                  ]}
                  style={{ marginBottom: 0 }}
                >
                  <Input placeholder="예: 빨강-노랑" />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item
                  name={`primaryColor_${scheme.id}`}
                  rules={[{ required: true, message: 'Primary 색상을 선택해주세요.' }]}
                  style={{ marginBottom: 0 }}
                >
                  <ColorPicker showText format="hex" />
                </Form.Item>
              </Col>
              <Col span={7}>
                <Form.Item
                  name={`secondaryColor_${scheme.id}`}
                  rules={[{ required: true, message: 'Secondary 색상을 선택해주세요.' }]}
                  style={{ marginBottom: 0 }}
                >
                  <ColorPicker showText format="hex" />
                </Form.Item>
              </Col>
              <Col span={1} style={{ textAlign: 'right' }}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(scheme.id)}
                  size="small"
                  style={{ padding: '4px' }}
                />
              </Col>
            </Row>
            {index < colorSchemes.length - 1 && (
              <Divider style={{ margin: '16px 0', borderColor: '#303030' }} />
            )}
          </div>
        ))}

        {colorSchemes.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: '#a6a6a6' }}>
            등록된 컬러스킴이 없습니다.
          </div>
        )}
      </Form>
    </Modal>
  );
};

export default ColorSchemeManager;
